/**
 * The Zoom client is created asynchronously by `ZoomClientProvider`, so the
 * Zoom contexts hold null until `client.init()` resolves. Screens that mount in
 * the same commit as the provider — a patient deep-linking straight into a
 * call — must wait for it rather than treat null as a failure.
 */
export const ZOOM_CLIENT_INIT_TIMEOUT_MS = 20_000;

export const ZOOM_CLIENT_CONNECTING_MESSAGE = "Connecting to the video service";

export const ZOOM_CLIENT_UNAVAILABLE_MESSAGE =
  "We could not start the video service. Please refresh the page and try joining again.";

/** Capture options shared by every place that starts the session camera. */
export const START_VIDEO_OPTIONS = {
  hd: true,
  fullHd: true,
  originalRatio: true,
} as const;

/** Audio options shared by every place that starts the session microphone. */
export const START_AUDIO_OPTIONS = {
  highBitrate: true,
} as const;

export const CAMERA_START_FAILED_MESSAGE =
  "We could not turn on your camera. Close any other app using it and try again.";

export const MICROPHONE_START_FAILED_MESSAGE =
  "We could not turn on your microphone. Close any other app using it and try again.";

/**
 * A meeting room may only be open in one tab. Tabs announce themselves to each
 * other on a per-room `BroadcastChannel`; a tab that gets an answer knows the
 * room is already held elsewhere.
 */
export const ROOM_TAB_CHANNEL_PREFIX = "telehealth-room:";

export const ROOM_TAB_MESSAGE = {
  PING: "PING",
  PONG: "PONG",
} as const;

/** How long a new tab waits for an answer before it claims the room itself. */
export const ROOM_TAB_CLAIM_TIMEOUT_MS = 500;

export const ROOM_ALREADY_OPEN_TITLE =
  "This appointment is already open in another tab";

export const ROOM_ALREADY_OPEN_MESSAGE =
  "Switch to the tab that already has this appointment open";

/** `client.init()` arguments; the SDK itself is loaded lazily by `useZoomClient`. */
export const ZOOM_CLIENT_INIT_LANGUAGE = "en-US";

export const ZOOM_CLIENT_INIT_DEPENDENT_ASSETS = "CDN";

export const ZOOM_CLIENT_INIT_OPTIONS = {
  stayAwake: true,
  patchJsMedia: true,
  leaveOnPageUnload: false,
  enforceVirtualBackground: true,
} as const;
